/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import axios from 'axios';

const apiBaseUrl = process.env.REACT_APP_BASE_API_URL || '';

interface User {
	id: string;
	email: string;
	rolename: string;
	fullname: string;
	accountstatus: string;
}

interface Resource {
	id: number;
	title: string;
	visibility: string;
	candownload: boolean;
}

interface Role {
	id: string;
	name: string;
}

export default function AdminPage() {
	const [users, setUsers] = useState<User[]>([]);
	const [resources, setResources] = useState<Resource[]>([]);
	const [roles, setRoles] = useState<Role[]>([]);
	const [selectedRoles, setSelectedRoles] = useState<Record<string, string>>(
		{}
	);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	//Auto-login using secret key if JWT not present
	useEffect(() => {
		const token = localStorage.getItem('token');

		if (token) {
			loadAdminData(token);
			return;
		}

		const params = new URLSearchParams(globalThis.location.search);
		const key = params.get('key');

		if (!key) {
			setError('Unauthorized access');
			setLoading(false);
			return;
		}

		axios
			.get(`${apiBaseUrl}/admin/autologin?key=${key}`)
			.then(res => {
				if (!res.data.success) {
					throw new Error('Admin auto-login failed');
				}

				const accessToken = res.data.data;

				localStorage.setItem('token', accessToken);

				globalThis.history.replaceState({}, '', '/admin');

				loadAdminData(accessToken);
			})
			.catch(err => {
				setError('Invalid or expired admin link');
				setLoading(false);
			});
	}, []);

	// Load users, resources & roles
	const loadAdminData = async (token: string) => {
		try {
			const [usersRes, resourcesRes, rolesRes] = await Promise.all([
				authGet(`${apiBaseUrl}/user`),
				authGet(`${apiBaseUrl}/resource`),
				authGet(`${apiBaseUrl}/role/rolelist`), // Fetch roles from API
			]);

			const guestUsers = usersRes.data.data.users.filter(
				(u: User) => u.rolename === 'guest'
			);

			setUsers(guestUsers);
			setResources(resourcesRes.data.data.resources);
			console.log('Resources fetched:', resourcesRes.data.data.resources);
			setRoles(rolesRes.data.data || []);

			// Initialize selected roles for each user (default to 'coachee')
			const initialRoles: Record<string, string> = {};

			guestUsers.forEach((u: User) => {
				const role = rolesRes.data.data.find(
					(r: Role) => r.name === u.rolename
				);
				if (role) {
					initialRoles[u.id] = role.id;
				}
			});

			setSelectedRoles(initialRoles);
		} catch (err: any) {
			console.error('Error loading admin data:', err);
			setError('Failed to load admin data');
		} finally {
			setLoading(false);
		}
	};

	// Approve user with selected role
	const approveUser = async (userId: string) => {
		const token = localStorage.getItem('token');
		const selectedRole = selectedRoles[userId];

		if (!selectedRole) return;

		try {
			await axios.put(
				`${apiBaseUrl}/user/${userId}`,
				{ roleid: selectedRole },
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			// Then, change status from pending-approval to active
			await axios.patch(
				`${apiBaseUrl}/user/${userId}/status`,
				{
					status: 'active',
					reason: 'Account approved by admin',
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			// Remove user from list after approval
			setUsers(prev => prev.filter(u => u.id !== userId));
		} catch (err) {
			console.error('Error approving user:', err);
			alert('Failed to approve user');
		}
	};

	const authGet = async (url: string) => {
		let token = localStorage.getItem('token');

		// Token missing or expired → try auto-login
		if (!token || isTokenExpired(token)) {
			const params = new URLSearchParams(globalThis.location.search);
			const key = params.get('key');

			if (!key) throw new Error('Session expired');

			token = await adminAutoLogin(key);
		}

		try {
			return await axios.get(url, {
				headers: { Authorization: `Bearer ${token}` },
			});
		} catch (err: any) {
			// Backend rejected token → retry once
			if (err.response?.status === 401 || err.response?.status === 403) {
				localStorage.removeItem('token');

				const params = new URLSearchParams(globalThis.location.search);
				const key = params.get('key');

				if (!key) throw err;

				const newToken = await adminAutoLogin(key);
				return axios.get(url, {
					headers: { Authorization: `Bearer ${newToken}` },
				});
			}
			throw err;
		}
	};

	const isTokenExpired = (token: string) => {
		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			return payload.exp * 1000 < Date.now();
		} catch {
			return true;
		}
	};

	const adminAutoLogin = async (key: string): Promise<string> => {
		const res = await axios.get(`${apiBaseUrl}/admin/autologin?key=${key}`);

		if (!res.data.success) {
			throw new Error('Admin auto-login failed');
		}

		const token = res.data.data;
		localStorage.setItem('token', token);
		return token;
	};

	// Enable / Disable resource
	const toggleResource = async (resourceId: number, is_enabled: boolean) => {
		const token = localStorage.getItem('token');
		if (!token) return;

		try {
			await axios.put(
				`${apiBaseUrl}/resource/${resourceId}`,
				{
					candownload: is_enabled,
					visibility: is_enabled ? 'public' : 'private',
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			setResources(prev =>
				prev.map(r =>
					r.id === resourceId
						? {
								...r,
								candownload: is_enabled,
								visibility: is_enabled ? 'public' : 'private',
							}
						: r
				)
			);
		} catch (err) {
			console.error('Error toggling resource:', err);
			alert('Failed to update resource');
		}
	};

	if (loading) {
		return (
			<div style={styles.container}>
				<div style={styles.loading}>Loading Admin Panel...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div style={styles.container}>
				<div style={styles.error}>
					<h3>{error}</h3>
				</div>
			</div>
		);
	}

	return (
		<div style={styles.container}>
			<h1 style={styles.title}>Admin Panel</h1>

			<div style={styles.section}>
				<h2 style={styles.sectionTitle}>Pending User Approvals</h2>
				{users
					.filter(user => user.rolename === 'guest')
					.map(user => (
						<div key={user.id} style={styles.tableRow}>
							<div style={styles.cell}>{user.email}</div>
							<div style={styles.cell}>{user.fullname}</div>

							<div style={styles.cell}>
								<span style={styles.statusBadge}>
									{user.accountstatus}
								</span>
							</div>

							<div style={styles.cell}>
								<select
									style={styles.select}
									value={
										selectedRoles[user.id] ?? user.rolename
									}
									onChange={e =>
										setSelectedRoles(prev => ({
											...prev,
											[user.id]: e.target.value,
										}))
									}
								>
									{roles
										.filter(role =>
											[
												'mentee',
												'coachee',
												'guest',
											].includes(role.name)
										)
										.map(role => (
											<option
												key={role.id}
												value={role.id}
											>
												{role.name}
											</option>
										))}
								</select>
							</div>

							<div style={styles.cell}>
								<button
									style={styles.approveButton}
									onClick={() => approveUser(user.id)}
								>
									Approve
								</button>
							</div>
						</div>
					))}
			</div>

			<div style={styles.section}>
				<h2 style={styles.sectionTitle}>Resource Management</h2>
				{resources.length === 0 ? (
					<p style={styles.emptyState}>No resources found</p>
				) : (
					<div style={styles.resourceGrid}>
						{resources.map(res => (
							<div key={res.id} style={styles.resourceCard}>
								<div style={styles.resourceInfo}>
									<h3 style={styles.resourceTitle}>
										{res.title}
									</h3>
									<span
										style={{
											...styles.resourceStatus,
											color: res.candownload
												? '#10b981'
												: '#ef4444',
										}}
									>
										{res.candownload
											? '● Enabled'
											: '● Disabled'}
									</span>
								</div>
								<label style={styles.toggle}>
									<input
										type="checkbox"
										checked={res.candownload}
										onChange={e =>
											toggleResource(
												res.id,
												e.target.checked
											)
										}
										style={styles.toggleInput}
									/>
									<span
										style={{
											...styles.toggleSlider,
											backgroundColor: res.candownload
												? '#10b981'
												: '#cbd5e1',
										}}
									></span>
								</label>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

const styles: Record<string, React.CSSProperties> = {
	container: {
		maxWidth: '1200px',
		margin: '0 auto',
		padding: '40px 20px',
		fontFamily:
			'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
	},
	title: {
		fontSize: '32px',
		fontWeight: '700',
		marginBottom: '40px',
		color: '#1f2937',
	},
	section: {
		marginBottom: '48px',
	},
	sectionTitle: {
		fontSize: '20px',
		fontWeight: '600',
		marginBottom: '20px',
		color: '#374151',
	},
	loading: {
		textAlign: 'center',
		padding: '60px 20px',
		fontSize: '18px',
		color: '#6b7280',
	},
	error: {
		padding: '40px',
		textAlign: 'center',
		color: '#dc2626',
		backgroundColor: '#fee2e2',
		borderRadius: '8px',
	},
	emptyState: {
		padding: '40px',
		textAlign: 'center',
		color: '#9ca3af',
		backgroundColor: '#f9fafb',
		borderRadius: '8px',
		border: '1px dashed #d1d5db',
	},
	table: {
		backgroundColor: 'white',
		borderRadius: '8px',
		overflow: 'hidden',
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
	},
	tableHeader: {
		display: 'grid',
		gridTemplateColumns: '2fr 2fr 1fr 1.5fr 1fr',
		gap: '16px',
		padding: '16px 20px',
		backgroundColor: '#f9fafb',
		borderBottom: '1px solid #e5e7eb',
	},
	headerCell: {
		fontWeight: '600',
		fontSize: '14px',
		color: '#6b7280',
		textTransform: 'uppercase' as const,
	},
	tableRow: {
		display: 'grid',
		gridTemplateColumns: '2fr 2fr 1fr 1.5fr 1fr',
		gap: '16px',
		padding: '16px 20px',
		borderBottom: '1px solid #f3f4f6',
		alignItems: 'center',
	},
	cell: {
		fontSize: '14px',
		color: '#374151',
	},
	statusBadge: {
		display: 'inline-block',
		padding: '4px 12px',
		backgroundColor: '#dbeafe',
		color: '#1e40af',
		borderRadius: '12px',
		fontSize: '12px',
		fontWeight: '500',
		textTransform: 'capitalize' as const,
	},
	select: {
		width: '100%',
		padding: '8px 12px',
		border: '1px solid #d1d5db',
		borderRadius: '6px',
		fontSize: '14px',
		backgroundColor: 'white',
		cursor: 'pointer',
		outline: 'none',
	},
	approveButton: {
		padding: '8px 20px',
		backgroundColor: '#10b981',
		color: 'white',
		border: 'none',
		borderRadius: '6px',
		fontSize: '14px',
		fontWeight: '500',
		cursor: 'pointer',
		transition: 'background-color 0.2s',
	},
	resourceGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
		gap: '20px',
	},
	resourceCard: {
		padding: '20px',
		backgroundColor: 'white',
		borderRadius: '8px',
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	resourceInfo: {
		flex: 1,
	},
	resourceTitle: {
		fontSize: '16px',
		fontWeight: '600',
		color: '#1f2937',
		marginBottom: '8px',
	},
	resourceStatus: {
		fontSize: '14px',
		fontWeight: '500',
	},
	toggle: {
		position: 'relative' as const,
		display: 'inline-block',
		width: '52px',
		height: '28px',
	},
	toggleInput: {
		opacity: 0,
		width: 0,
		height: 0,
	},
	toggleSlider: {
		position: 'absolute' as const,
		cursor: 'pointer',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		borderRadius: '28px',
		transition: '0.3s',
	},
};
